import { useState } from "react";

import ChevronDownIcon from "@assets/icons/chevron-down.svg";
import PlusIcon from "@assets/icons/plus.svg";

import useDialogStore from "@store/useDialogStore";
import { ICompany } from "@interfaces/company.interface";
import { cn } from "@utils/tailwind.util";
import { FormLabel } from "@ui/FormElements";
import { Counter } from "@ui/Counter";
import { Button } from "@ui/Button";
import { AnimatedHeight } from "@ui/AnimatedHeight";
import { DomainRow } from "./DomainRow";
import { ConfirmChangePrimaryModal } from "./ConfirmChangePrimaryModal";
import { ConfirmRemoveDomainModal } from "./ConfirmRemoveDomainModal";
import { MAX_ALTERNATIVE_DOMAINS, useWebsiteDomainsForm } from "../hooks/useWebsiteDomainsForm";

const buttonClassName =
  "flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80 focus:dark:bg-gray-600";

interface Props {
  company: ICompany;
}

export function WebsiteDomainsForm({ company }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { openDialog } = useDialogStore();
  const {
    register,
    formState,
    fields,
    badgeCount,
    canAddMore,
    addRow,
    discardUnsaved,
    commitAlternative,
    preparePrimaryEdit,
    updatePrimaryDomain,
    resetPrimary,
    promoteToPrimary,
    resolveRemoveTarget,
    deleteAlternative,
  } = useWebsiteDomainsForm({ company });
  const { errors } = formState;

  const addDisabled = !canAddMore;
  const addDisabledReason =
    badgeCount >= MAX_ALTERNATIVE_DOMAINS
      ? `Maximum of ${MAX_ALTERNATIVE_DOMAINS} domains reached`
      : "Finish the empty row first";

  const toggleExpanded = () => {
    setIsExpanded((prev) => {
      if (prev) discardUnsaved();
      return !prev;
    });
  };

  const handlePrimaryCommit = async () => {
    const next = await preparePrimaryEdit();
    if (!next) return;
    openDialog(
      <ConfirmChangePrimaryModal
        onConfirm={() => void updatePrimaryDomain(next)}
        onCancel={resetPrimary}
      />,
      // Buttons only: a click-outside dismiss would skip resetPrimary and leave the typed value.
      { disableOutsideClick: true }
    );
  };

  const handlePromote = (index: number) =>
    openDialog(<ConfirmChangePrimaryModal onConfirm={() => void promoteToPrimary(index)} />);

  const handleRemove = (index: number) => {
    const target = resolveRemoveTarget(index);
    if (!target) return;
    openDialog(
      <ConfirmRemoveDomainModal
        domain={target.domain}
        counterparts={target.counterparts}
        onConfirm={() => void deleteAlternative(target.domain)}
      />
    );
  };

  return (
    <div className="flex w-full flex-col items-start gap-2 font-inter">
      <FormLabel>Website</FormLabel>

      <DomainRow
        field={register("primary")}
        error={errors.primary?.message}
        onCommit={() => void handlePrimaryCommit()}
        trailing={
          <>
            {badgeCount > 0 && <Counter value={badgeCount} />}
            <button
              type="button"
              aria-label={isExpanded ? "Hide domains" : "Show domains"}
              onClick={toggleExpanded}
              className="flex flex-shrink-0 items-center justify-center"
            >
              <ChevronDownIcon
                className={cn("h-4 w-4 text-content-secondary transition-transform", {
                  "-rotate-90": !isExpanded,
                })}
              />
            </button>
          </>
        }
      />

      <AnimatedHeight className="-mt-2">
      {/* -mt-2 compensates pt-2, last one is needed for ResizeObserver for animation */ }
        {isExpanded && (
          <div className="flex w-full flex-col gap-2 pt-2">
            {fields.map((field, index) => (
              <DomainRow
                key={field.id}
                field={register(`alternatives.${index}.value`)}
                error={errors.alternatives?.[index]?.value?.message}
                onCommit={() => void commitAlternative(index)}
                autoStartEditing={index >= badgeCount}
                showMakePrimary={index < badgeCount}
                onMakePrimary={() => handlePromote(index)}
                onRemove={() => handleRemove(index)}
              />
            ))}

            <Button
              buttonType="SECONDARY"
              PrefixIcon={PlusIcon}
              className={buttonClassName}
              onClick={addRow}
              isDisabled={addDisabled}
              title={addDisabled ? addDisabledReason : undefined}
            >
              Add new domain
            </Button>
          </div>
        )}
      </AnimatedHeight>
    </div>
  );
}
